import { AdvancedTagFilterCollection, ICampaignsCreatedNotificationInfo } from '@AppModels/.';
import { IOperationResultNoValue, VmUtils } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { computed, makeObservable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IUserSessionComponentProps, UserSessionViewModelKey } from '../../../../models/AppState';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../models/Logging';
import { isTagSearchContactFilterCriteria } from '../../../../models/UiUtils';
import { CreateCampaignApprovalViewModel } from '../../../../viewmodels/AdminViewModels';
import { DeprecatedTextArea } from '../../../../web/components/DeprecatedTextArea';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import {
	INotificationServiceComponentProps,
	withNotificationService,
} from '../../../../web/components/LocalNotificationObserver/WithNotificationService';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { GroupEmailTags } from '../../../../web/components/reporting/emails/GroupEmailTags';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps
	extends IEventLoggingComponentProps,
		IUserSessionComponentProps,
		INotificationServiceComponentProps<ICampaignsCreatedNotificationInfo> {
	approval: CreateCampaignApprovalViewModel;
	className?: string;
	onNotify?(): void;
	styles?: StyleDeclarationValue[];
}

class _BulkEmailApprovalSendNote extends React.Component<IProps> {
	private mNoteFieldRef: HTMLTextAreaElement;

	constructor(props: IProps) {
		super(props);

		makeObservable<_BulkEmailApprovalSendNote, 'tagFilters'>(this, {
			tagFilters: computed,
		});
	}

	public componentDidMount() {
		const { logPageView } = this.props;

		logPageView('BulkEmailSendNote');
	}

	public render() {
		const { approval, className, styles } = this.props;
		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} bulk-email-send-note ${className || ''}`}>
				<MultiContainerHeader fullscreenHeader='Schedule Email for Clients: Send Note' />
				<div className={css(styleSheet.content)}>
					<span className={css(styleSheet.title)}>Send a Note</span>
					<span className={css(styleSheet.message)}>
						{
							"If you are done, leave a message for the client(s) below. Levitate will send this along with the template you've picked out "
						}
						<b>from your email.</b>
					</span>
					<div className={css(styleSheet.textAreaContainer)}>
						<svg
							className={css(styleSheet.image)}
							width='399'
							height='303'
							fill='none'
							xmlns='http://www.w3.org/2000/svg'
						>
							<path
								d='M288 268a7 7 0 100-14 7 7 0 000 14zM128 51a7 7 0 100-14 7 7 0 000 14zM68 303c37.555 0 68-4.477 68-10s-30.445-10-68-10-68 4.477-68 10 30.445 10 68 10z'
								fill='#F1F1F1'
							/>
							<path d='M62 92c8.837 0 16-7.163 16-16s-7.163-16-16-16-16 7.163-16 16 7.163 16 16 16z' fill='#2F2E41' />
							<path
								d='M44.208 275.765L41 289h7.792L52 274l-7.792 1.765zM81 275v13.067l8 .933v-14h-8zM43 139.429s.91 18.495 1.818 19.785C45.728 160.505 53.91 179 53.91 179L58 166.957s-6.364-9.893-5.455-15.485c.91-5.591.337-12.472.337-12.472l-9.882.429zM69 93c6.627 0 12-5.373 12-12s-5.373-12-12-12-12 5.373-12 12 5.373 12 12 12z'
								fill='#9F616A'
							/>
							<path
								d='M57.802 83s.418 17.159-2.505 19.733c-2.923 2.574 16.703.429 16.703.429s-4.176-12.011-.835-14.585C74.505 86.003 57.802 83 57.802 83z'
								fill='#9F616A'
							/>
							<path
								d='M74.871 100.404s-2.618-4.813-6.11-4.376c-3.492.438-14.84 1.313-15.277 2.188-.436.876-1.746 3.939-2.619 4.814-.873.875-7.42 2.188-7.856 7.44-.437 5.251 15.713 37.198 15.713 37.198s-2.183 7.44-1.746 9.19c.436 1.751-2.182 7.002-1.746 9.19.437 2.189-6.547 15.317-3.055 32.385v27.57s-11.785 47.702-8.293 51.203c3.491 3.501 15.276 1.312 17.459 0 2.182-1.313 11.348-67.395 11.348-67.395l4.365-22.319.436 49.014s-.873 41.137 2.183 42.45c3.055 1.313 14.84 1.751 15.713-.875.68-2.048 4.016-56.537 5.452-80.421a89.48 89.48 0 00-1.922-24.584c-1.817-8.262-4.454-17.968-7.459-21.907-5.674-7.44-9.165-31.071-9.165-31.071l7.42-13.567s-4.365-4.376-6.984-4.376c-2.619 0-7.857-1.751-7.857-1.751z'
								fill='#2F2E41'
							/>
							<path
								d='M45.955 109l-2.174 1.547s-1.536 27.77-.3 29.494c1.237 1.725 9.837.883 10.484-.636.648-1.519-8.01-30.405-8.01-30.405z'
								fill='#2F2E41'
							/>
							<path
								d='M81 135.614s.41 17.991 2.873 23.131c2.462 5.14 11.49 26.557 11.49 26.557s-.523 23.572 4.048 17.783c4.571-5.79 1.698-19.496 1.698-19.496l-7.797-31.698s-4.104-14.135-3.694-18.419c.41-4.283-8.618 2.142-8.618 2.142z'
								fill='#9F616A'
							/>
							<path
								d='M88.785 282.043s.441-.358 1.765.988c1.324 1.345 4.853 3.14 4.412 8.074-.441 4.934-3.088 5.831-3.088 5.831v-1.345H90.55v2.243s-2.206 7.177-12.354 2.691c0 0-.441-3.589 0-5.831.441-2.243.441-13.009 1.765-12.561 1.324.449 4.853.898 5.736 2.692.882 1.794 3.53 1.165 3.088-2.782zm-44.57 0s-.441-.358-1.765.988c-1.324 1.345-4.853 3.14-4.412 8.074.441 4.934 3.088 5.831 3.088 5.831v-1.345h1.324v2.243s2.206 7.177 12.354 2.691c0 0 .441-3.589 0-5.831-.441-2.243-.441-13.009-1.765-12.561-1.324.449-4.853.898-5.736 2.692-.882 1.794-3.53 1.165-3.088-2.782zM83.94 105l5.794 1.37s0 10.046 1.655 14.155c1.655 4.11 4.552 13.699 3.31 14.156-1.241.457-12.827 6.85-13.655 3.196-.827-3.653-2.896-32.877 2.897-32.877zM45 70c8.837 0 16-7.163 16-16s-7.163-16-16-16-16 7.163-16 16 7.163 16 16 16z'
								fill='#2F2E41'
							/>
							<path
								d='M32.197 67.278a15.839 15.839 0 0014.58 2.227C51.703 67.8 55.514 63.774 57 58.7c-.755 6.11-4.824 11.268-10.523 13.344-5.7 2.075-12.063.715-16.459-3.52-4.395-4.234-6.077-10.622-4.35-16.524-.733 5.924 1.77 11.782 6.529 15.278z'
								fill='#2F2E41'
							/>
							<path
								d='M58.5 90C64.851 90 70 84.851 70 78.5S64.851 67 58.5 67 47 72.149 47 78.5 52.149 90 58.5 90z'
								fill='#2F2E41'
							/>
							<path
								d='M68.5 82c5.799 0 10.5-3.358 10.5-7.5 0-4.142-4.701-7.5-10.5-7.5S58 70.358 58 74.5c0 4.142 4.701 7.5 10.5 7.5z'
								fill='#2F2E41'
							/>
							<path d='M71.5 87c.828 0 1.5-1.79 1.5-4s-.672-4-1.5-4-1.5 1.79-1.5 4 .672 4 1.5 4z' fill='#A0616A' />
							<path d='M176.278.242L398.224 67.05l-53.543 178.735-221.946-66.808L176.278.242z' fill='#F1F1F1' />
							<path fill='#046CB6' d='M139 69h78v98h-78z' />
							<path
								fill='#F9F9F9'
								d='M149.706 91.969h56.588v3.063h-56.588zm0 9.187h56.588v3.063h-56.588zm0 9.188h56.588v3.063h-56.588zm0 9.187h56.588v3.063h-56.588z'
							/>
						</svg>
						<DeprecatedTextArea
							inputClassName={css(styleSheet.inputField, approval.isBusy ? baseStyleSheet.disableInteraction : null)}
							inputId='bulk-email-send-note-input'
							inputRef={this.onNoteFieldRef}
							onChange={this.onTextChanged}
							value={approval.note || ''}
						/>
						{!!approval.isBusy && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />}
					</div>
					<div className={css(styleSheet.infoContainer)}>
						<div>
							<span className={css(styleSheet.infoTitle)}>Recipients: </span>

							{approval?.selectedUsers.map((x, idx, arr) => x.firstName + (idx === arr.length - 1 ? '' : ', '))}
						</div>
						<div className={css(styleSheet.infoTagContainer)}>
							<span className={css(styleSheet.infoTitle)}>Tag:</span>
							<GroupEmailTags tags={this.tagFilters?.filters?.map(x => x.tag)} />
						</div>
						<div>
							<span className={css(styleSheet.infoTitle)}>Template Name:</span>{' '}
							{approval?.emailContent?.template?.name || 'Custom'}
						</div>
					</div>
					<div className={css(styleSheet.buttonContainer)}>
						<button className={css(baseStyleSheet.ctaButton)} disabled={!!approval.isBusy} onClick={this.onNotify}>
							<span>Notify Client</span>
						</button>
					</div>
				</div>
			</div>
		);
	}

	private get tagFilters() {
		const { approval } = this.props;
		if (approval?.contactsFilterRequest?.filter?.criteria) {
			const sorted = VmUtils.sortContactFilterCriteria(approval.contactsFilterRequest?.filter.criteria);
			if (sorted.searches.length > 0) {
				const tagSearches = sorted.searches.filter(x => isTagSearchContactFilterCriteria(x));
				if (tagSearches.length > 0) {
					const filters = AdvancedTagFilterCollection.instanceWithCriteria(tagSearches);
					filters.sort();
					return filters;
				}
			}
		}
		return null;
	}

	private onNoteFieldRef = (ref?: HTMLTextAreaElement) => {
		this.mNoteFieldRef = ref;
	};

	private onTextChanged = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		const { approval } = this.props;
		approval.setNote(event.target.value);
	};

	private onNotify = () => {
		const { logApiError, logEvent, onNotify, approval, postNotification } = this.props;

		logEvent('SendForApproval', {
			subject: approval.emailContent?.subject,
			templateId: approval.emailContent?.template?.id,
		});
		this.mNoteFieldRef?.blur();
		approval
			.sendForApproval()
			?.then(campaigns => {
				if (onNotify) {
					onNotify();
				}
				// tell listeners campaigns were created
				const info: ICampaignsCreatedNotificationInfo = {
					accountId: approval.account.id,

					campaigns: campaigns.map(x => x.toJs()),
				};

				postNotification({
					info,
					topic: Topics.CREATE_CAMPAIGNS_ITEM,
				});
			})
			.catch((error: IOperationResultNoValue) => {
				logApiError('SendForApproval-Error', error);
			});
	};
}

const BulkEmailApprovalSendNoteAsObserver = observer(_BulkEmailApprovalSendNote);
const BulkEmailApprovalSendNoteWithLocalNotifications = withNotificationService(BulkEmailApprovalSendNoteAsObserver);
const BulkEmailApprovalSendNoteWithContext = inject(UserSessionViewModelKey)(
	BulkEmailApprovalSendNoteWithLocalNotifications
);
export const BulkEmailApprovalSendNote = withEventLogging(
	BulkEmailApprovalSendNoteWithContext,
	'BulkEmailApprovalSendNote'
);
